/**
 * Image Cleanup Service
 * 
 * Automatically cleans up unused images from storage on login.
 * Keeps images that are:
 * - Referenced in display_layers (in use)
 * - Among the N most recently uploaded
 */

import { supabase } from '@/lib/supabase'
import { logger } from '@/lib/logger'

const KEEP_RECENT_COUNT = 10
const CLEANUP_COOLDOWN_KEY = 'ambi-image-cleanup-last-run'
const CLEANUP_COOLDOWN_MS = 24 * 60 * 60 * 1000 // 24 hours

interface CleanupResult {
  success: boolean
  deleted?: number
  freedMB?: string
  kept?: { inUse: number; recent: number }
  error?: string
  skipped?: boolean
  reason?: string
}

/**
 * Check if cleanup should run (based on cooldown)
 */
function shouldRunCleanup(): boolean {
  try {
    const lastRun = localStorage.getItem(CLEANUP_COOLDOWN_KEY)
    if (!lastRun) return true
    
    const lastRunTime = parseInt(lastRun, 10)
    const now = Date.now()
    
    return now - lastRunTime >= CLEANUP_COOLDOWN_MS
  } catch {
    return true
  }
}

/**
 * Mark cleanup as run
 */
function markCleanupRun(): void {
  try {
    localStorage.setItem(CLEANUP_COOLDOWN_KEY, Date.now().toString())
  } catch {
    // Ignore storage errors
  }
}

/**
 * Run image cleanup
 * This is called automatically on login with a 24-hour cooldown
 */
export async function runImageCleanup(force = false): Promise<CleanupResult> {
  // Check cooldown unless forced
  if (!force && !shouldRunCleanup()) {
    logger.debug('[ImageCleanup] Skipping - ran recently')
    return { success: true, skipped: true, reason: 'Ran within last 24 hours' }
  }

  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session?.access_token) {
      return { success: false, error: 'Not authenticated' }
    }

    // Determine the functions URL
    // In development with Vite proxy, use relative URL
    // In production, use the full Supabase URL
    const isDev = import.meta.env.DEV
    const functionsUrl = isDev 
      ? '/functions/v1/cleanup-images'
      : `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/cleanup-images`

    logger.info('[ImageCleanup] Starting automatic cleanup...')

    const response = await fetch(functionsUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        dryRun: false,
        keepRecentCount: KEEP_RECENT_COUNT
      })
    })

    const result = await response.json()

    if (result.success) {
      markCleanupRun()
      
      if (result.deleted > 0) {
        logger.info(`[ImageCleanup] Cleaned up ${result.deleted} images, freed ${result.freedMB} MB`)
      } else {
        logger.debug('[ImageCleanup] No unused images to clean up')
      }
      
      return {
        success: true,
        deleted: result.deleted,
        freedMB: result.freedMB,
        kept: result.kept
      }
    } else {
      logger.warn('[ImageCleanup] Cleanup failed:', result.error)
      return { success: false, error: result.error }
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error'
    logger.error('[ImageCleanup] Error:', errorMessage)
    return { success: false, error: errorMessage }
  }
}

/**
 * Preview what would be cleaned up (dry run)
 */
export async function previewImageCleanup(): Promise<CleanupResult & { 
  wouldDelete?: number
  wouldFreeMB?: string
  images?: Array<{ id: string; name: string; sizeMB: string }>
}> {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session?.access_token) {
      return { success: false, error: 'Not authenticated' }
    }

    const isDev = import.meta.env.DEV
    const functionsUrl = isDev 
      ? '/functions/v1/cleanup-images'
      : `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/cleanup-images`

    const response = await fetch(functionsUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        dryRun: true,
        keepRecentCount: KEEP_RECENT_COUNT
      })
    })

    return await response.json()
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error'
    return { success: false, error: errorMessage }
  }
}
